/**
 * @file     bcCities.ts
 * @purpose  Province-wide city registry keyed off `city`, the only geography
 *           column populated on every feed (`mls_area_major` is null on all
 *           DDF rows, which is 31% of displayable inventory and 100% of towns
 *           like Prince George). A city is applied as a single `city:<slug>`
 *           token inside the existing `regions` filter array; the service
 *           expands it to one `city.ilike` condition. Generated from live
 *           displayable inventory by scripts/mls/generate-bc-cities.ts.
 */

import type { BcRegionSlug } from './bcAreas';

export interface BcCity {
  slug: string;
  /** Exact `city` value as it appears in the feed. */
  name: string;
  region: BcRegionSlug;
  /** Displayable listings at generation time; used for ordering only. */
  count: number;
}

/** Prefix marking a `regions` entry as a whole city (vs an area slug or `bc:` region). */
export const CITY_PREFIX = 'city:';
export const cityToken = (slug: string): string => `${CITY_PREFIX}${slug}`;
export const parseCityToken = (token: string): string | null =>
  token.startsWith(CITY_PREFIX) ? token.slice(CITY_PREFIX.length) : null;

/** Ordered by inventory (most listings first). */
export const BC_CITIES: readonly BcCity[] = [
  { slug: 'surrey', name: 'Surrey', region: 'metro-vancouver', count: 6047 },
  { slug: 'vancouver', name: 'Vancouver', region: 'metro-vancouver', count: 4711 },
  { slug: 'richmond', name: 'Richmond', region: 'metro-vancouver', count: 2108 },
  { slug: 'kelowna', name: 'Kelowna', region: 'okanagan-thompson', count: 2081 },
  { slug: 'langley', name: 'Langley', region: 'metro-vancouver', count: 2077 },
  { slug: 'burnaby', name: 'Burnaby', region: 'metro-vancouver', count: 2039 },
  { slug: 'abbotsford', name: 'Abbotsford', region: 'fraser-valley', count: 1800 },
  { slug: 'coquitlam', name: 'Coquitlam', region: 'metro-vancouver', count: 1349 },
  { slug: 'chilliwack', name: 'Chilliwack', region: 'fraser-valley', count: 1320 },
  { slug: 'kamloops', name: 'Kamloops', region: 'okanagan-thompson', count: 1138 },
  { slug: 'north-vancouver', name: 'North Vancouver', region: 'metro-vancouver', count: 1048 },
  { slug: 'maple-ridge', name: 'Maple Ridge', region: 'metro-vancouver', count: 1012 },
  { slug: 'delta', name: 'Delta', region: 'metro-vancouver', count: 935 },
  { slug: 'mission', name: 'Mission', region: 'fraser-valley', count: 908 },
  { slug: 'vernon', name: 'Vernon', region: 'okanagan-thompson', count: 884 },
  { slug: 'prince-george', name: 'Prince George', region: 'northern-bc', count: 837 },
  { slug: 'nanaimo', name: 'Nanaimo', region: 'vancouver-island', count: 834 },
  { slug: 'new-westminster', name: 'New Westminster', region: 'metro-vancouver', count: 735 },
  { slug: 'west-kelowna', name: 'West Kelowna', region: 'okanagan-thompson', count: 689 },
  { slug: 'west-vancouver', name: 'West Vancouver', region: 'metro-vancouver', count: 600 },
  { slug: 'saanich', name: 'Saanich', region: 'vancouver-island', count: 595 },
  { slug: 'langford', name: 'Langford', region: 'vancouver-island', count: 592 },
  { slug: 'victoria', name: 'Victoria', region: 'vancouver-island', count: 572 },
  { slug: 'penticton', name: 'Penticton', region: 'okanagan-thompson', count: 504 },
  { slug: 'white-rock', name: 'White Rock', region: 'metro-vancouver', count: 489 },
  { slug: 'port-coquitlam', name: 'Port Coquitlam', region: 'metro-vancouver', count: 449 },
  { slug: 'osoyoos', name: 'Osoyoos', region: 'okanagan-thompson', count: 404 },
  { slug: 'port-moody', name: 'Port Moody', region: 'metro-vancouver', count: 353 },
  { slug: 'campbell-river', name: 'Campbell River', region: 'vancouver-island', count: 321 },
  { slug: 'whistler', name: 'Whistler', region: 'sea-to-sky-sunshine-coast', count: 317 },
  { slug: 'sooke', name: 'Sooke', region: 'vancouver-island', count: 311 },
  { slug: 'squamish', name: 'Squamish', region: 'sea-to-sky-sunshine-coast', count: 295 },
  { slug: 'salmon-arm', name: 'Salmon Arm', region: 'okanagan-thompson', count: 294 },
  { slug: 'courtenay', name: 'Courtenay', region: 'vancouver-island', count: 289 },
  { slug: 'sechelt', name: 'Sechelt', region: 'sea-to-sky-sunshine-coast', count: 284 },
  { slug: 'fort-st-john', name: 'Fort St. John', region: 'northern-bc', count: 275 },
  { slug: 'lake-country', name: 'Lake Country', region: 'okanagan-thompson', count: 274 },
  { slug: 'port-alberni', name: 'Port Alberni', region: 'vancouver-island', count: 273 },
  { slug: 'duncan', name: 'Duncan', region: 'vancouver-island', count: 264 },
  { slug: 'quesnel', name: 'Quesnel', region: 'northern-bc', count: 238 },
  { slug: 'merritt', name: 'Merritt', region: 'okanagan-thompson', count: 224 },
  { slug: 'powell-river', name: 'Powell River', region: 'sea-to-sky-sunshine-coast', count: 222 },
  { slug: 'oliver', name: 'Oliver', region: 'okanagan-thompson', count: 209 },
  { slug: 'parksville', name: 'Parksville', region: 'vancouver-island', count: 205 },
  { slug: 'gibsons', name: 'Gibsons', region: 'sea-to-sky-sunshine-coast', count: 201 },
  { slug: 'summerland', name: 'Summerland', region: 'okanagan-thompson', count: 199 },
  { slug: 'dawson-creek', name: 'Dawson Creek', region: 'northern-bc', count: 186 },
  { slug: 'qualicum-beach', name: 'Qualicum Beach', region: 'vancouver-island', count: 185 },
  { slug: 'pitt-meadows', name: 'Pitt Meadows', region: 'metro-vancouver', count: 184 },
  { slug: 'nelson', name: 'Nelson', region: 'kootenays-rockies', count: 184 },
  { slug: 'williams-lake', name: 'Williams Lake', region: 'northern-bc', count: 169 },
  { slug: 'hope', name: 'Hope', region: 'fraser-valley', count: 160 },
  { slug: 'salt-spring', name: 'Salt Spring', region: 'vancouver-island', count: 158 },
  { slug: 'cranbrook', name: 'Cranbrook', region: 'kootenays-rockies', count: 154 },
  { slug: 'grand-forks', name: 'Grand Forks', region: 'kootenays-rockies', count: 150 },
  { slug: 'castlegar', name: 'Castlegar', region: 'kootenays-rockies', count: 150 },
  { slug: 'peachland', name: 'Peachland', region: 'okanagan-thompson', count: 145 },
  { slug: 'ladysmith', name: 'Ladysmith', region: 'vancouver-island', count: 140 },
  { slug: 'revelstoke', name: 'Revelstoke', region: 'kootenays-rockies', count: 139 },
  { slug: 'central-saanich', name: 'Central Saanich', region: 'vancouver-island', count: 137 },
  { slug: 'esquimalt', name: 'Esquimalt', region: 'vancouver-island', count: 134 },
  { slug: '100-mile-house', name: '100 Mile House', region: 'northern-bc', count: 134 },
  { slug: 'colwood', name: 'Colwood', region: 'vancouver-island', count: 126 },
  { slug: 'oak-bay', name: 'Oak Bay', region: 'vancouver-island', count: 126 },
  { slug: 'terrace', name: 'Terrace', region: 'northern-bc', count: 126 },
  { slug: 'enderby', name: 'Enderby', region: 'okanagan-thompson', count: 119 },
  { slug: 'tsawwassen', name: 'Tsawwassen', region: 'metro-vancouver', count: 118 },
  { slug: 'blind-bay', name: 'Blind Bay', region: 'okanagan-thompson', count: 117 },
  { slug: 'ucluelet', name: 'Ucluelet', region: 'vancouver-island', count: 114 },
  { slug: 'princeton', name: 'Princeton', region: 'okanagan-thompson', count: 107 },
  { slug: 'invermere', name: 'Invermere', region: 'kootenays-rockies', count: 106 },
  { slug: 'rossland', name: 'Rossland', region: 'kootenays-rockies', count: 105 },
  { slug: 'radium-hot-springs', name: 'Radium Hot Springs', region: 'kootenays-rockies', count: 103 },
  { slug: 'agassiz', name: 'Agassiz', region: 'fraser-valley', count: 103 },
  { slug: 'bowen-island', name: 'Bowen Island', region: 'sea-to-sky-sunshine-coast', count: 101 },
  { slug: 'creston', name: 'Creston', region: 'kootenays-rockies', count: 99 },
  { slug: 'fernie', name: 'Fernie', region: 'kootenays-rockies', count: 99 },
  { slug: 'sidney', name: 'Sidney', region: 'vancouver-island', count: 98 },
  { slug: 'comox', name: 'Comox', region: 'vancouver-island', count: 98 },
  { slug: 'pemberton', name: 'Pemberton', region: 'sea-to-sky-sunshine-coast', count: 97 },
  { slug: 'golden', name: 'Golden', region: 'kootenays-rockies', count: 97 },
  { slug: 'fairmont-hot-springs', name: 'Fairmont Hot Springs', region: 'kootenays-rockies', count: 96 },
  { slug: 'kitimat', name: 'Kitimat', region: 'northern-bc', count: 95 },
  { slug: 'north-saanich', name: 'North Saanich', region: 'vancouver-island', count: 94 },
  { slug: 'harrison-hot-springs', name: 'Harrison Hot Springs', region: 'fraser-valley', count: 94 },
  { slug: 'coldstream', name: 'Coldstream', region: 'okanagan-thompson', count: 92 },
  { slug: 'view-royal', name: 'View Royal', region: 'vancouver-island', count: 92 },
  { slug: 'nanoose-bay', name: 'Nanoose Bay', region: 'vancouver-island', count: 91 },
  { slug: 'lumby', name: 'Lumby', region: 'okanagan-thompson', count: 89 },
  { slug: 'lake-cowichan', name: 'Lake Cowichan', region: 'vancouver-island', count: 87 },
  { slug: 'big-white', name: 'Big White', region: 'okanagan-thompson', count: 87 },
  { slug: 'sicamous', name: 'Sicamous', region: 'okanagan-thompson', count: 84 },
  { slug: 'trail', name: 'Trail', region: 'kootenays-rockies', count: 81 },
  { slug: 'chase', name: 'Chase', region: 'okanagan-thompson', count: 79 },
  { slug: 'valemount', name: 'Valemount', region: 'northern-bc', count: 79 },
  { slug: 'fort-nelson', name: 'Fort Nelson', region: 'northern-bc', count: 79 },
  { slug: 'chemainus', name: 'Chemainus', region: 'vancouver-island', count: 78 },
  { slug: 'keremeos', name: 'Keremeos', region: 'okanagan-thompson', count: 78 },
  { slug: 'mayne-island', name: 'Mayne Island', region: 'vancouver-island', count: 78 },
  { slug: 'shawnigan-lake', name: 'Shawnigan Lake', region: 'vancouver-island', count: 77 },
  { slug: 'naramata', name: 'Naramata', region: 'okanagan-thompson', count: 75 },
  { slug: 'gabriola-island', name: 'Gabriola Island', region: 'vancouver-island', count: 75 },
  { slug: 'anglemont', name: 'Anglemont', region: 'okanagan-thompson', count: 74 },
  { slug: 'clearwater', name: 'Clearwater', region: 'northern-bc', count: 74 },
  { slug: 'sun-peaks', name: 'Sun Peaks', region: 'okanagan-thompson', count: 73 },
  { slug: 'smithers', name: 'Smithers', region: 'northern-bc', count: 70 },
  { slug: 'burns-lake', name: 'Burns Lake', region: 'northern-bc', count: 69 },
  { slug: 'okanagan-falls', name: 'Okanagan Falls', region: 'okanagan-thompson', count: 66 },
  { slug: 'barriere', name: 'Barriere', region: 'okanagan-thompson', count: 65 },
  { slug: 'christina-lake', name: 'Christina Lake', region: 'kootenays-rockies', count: 64 },
  { slug: 'westbank', name: 'Westbank', region: 'okanagan-thompson', count: 64 },
  { slug: 'lantzville', name: 'Lantzville', region: 'vancouver-island', count: 64 },
  { slug: 'halfmoon-bay', name: 'Halfmoon Bay', region: 'sea-to-sky-sunshine-coast', count: 63 },
  { slug: '108-mile-ranch', name: '108 Mile Ranch', region: 'northern-bc', count: 62 },
  { slug: 'windermere', name: 'Windermere', region: 'kootenays-rockies', count: 61 },
  { slug: 'chetwynd', name: 'Chetwynd', region: 'northern-bc', count: 60 },
  { slug: 'tumbler-ridge', name: 'Tumbler Ridge', region: 'northern-bc', count: 57 },
  { slug: 'nakusp', name: 'Nakusp', region: 'kootenays-rockies', count: 57 },
  { slug: 'cobble-hill', name: 'Cobble Hill', region: 'vancouver-island', count: 56 },
  { slug: 'lac-la-hache', name: 'Lac La Hache', region: 'northern-bc', count: 55 },
  { slug: 'cumberland', name: 'Cumberland', region: 'vancouver-island', count: 54 },
  { slug: 'kimberley', name: 'Kimberley', region: 'kootenays-rockies', count: 53 },
  { slug: 'bridge-lake', name: 'Bridge Lake', region: 'northern-bc', count: 53 },
  { slug: 'sorrento', name: 'Sorrento', region: 'okanagan-thompson', count: 52 },
  { slug: 'port-hardy', name: 'Port Hardy', region: 'vancouver-island', count: 52 },
  { slug: 'lee-creek', name: 'Lee Creek', region: 'okanagan-thompson', count: 52 },
  { slug: 'prince-rupert', name: 'Prince Rupert', region: 'northern-bc', count: 51 },
  { slug: 'cultus-lake', name: 'Cultus Lake', region: 'fraser-valley', count: 51 },
  { slug: 'rosedale', name: 'Rosedale', region: 'fraser-valley', count: 50 },
  { slug: 'tappen', name: 'Tappen', region: 'okanagan-thompson', count: 50 },
  { slug: 'fraser-lake', name: 'Fraser Lake', region: 'northern-bc', count: 49 },
  { slug: 'pender-island', name: 'Pender Island', region: 'vancouver-island', count: 47 },
  { slug: 'lone-butte', name: 'Lone Butte', region: 'northern-bc', count: 46 },
  { slug: 'armstrong', name: 'Armstrong', region: 'okanagan-thompson', count: 45 },
  { slug: 'vanderhoof', name: 'Vanderhoof', region: 'northern-bc', count: 45 },
  { slug: 'galiano-island', name: 'Galiano Island', region: 'vancouver-island', count: 44 },
  { slug: 'scotch-creek', name: 'Scotch Creek', region: 'okanagan-thompson', count: 43 },
  { slug: 'garden-bay', name: 'Garden Bay', region: 'sea-to-sky-sunshine-coast', count: 43 },
  { slug: 'sparwood', name: 'Sparwood', region: 'kootenays-rockies', count: 42 },
  { slug: 'anmore', name: 'Anmore', region: 'metro-vancouver', count: 42 },
  { slug: 'sunshine-valley', name: 'Sunshine Valley', region: 'fraser-valley', count: 42 },
  { slug: 'logan-lake', name: 'Logan Lake', region: 'okanagan-thompson', count: 41 },
  { slug: 'roberts-creek', name: 'Roberts Creek', region: 'sea-to-sky-sunshine-coast', count: 41 },
  { slug: 'eagle-bay', name: 'Eagle Bay', region: 'okanagan-thompson', count: 40 },
  { slug: 'cowichan-bay', name: 'Cowichan Bay', region: 'vancouver-island', count: 40 },
  { slug: 'spallumcheen', name: 'Spallumcheen', region: 'okanagan-thompson', count: 39 },
  { slug: 'madeira-park', name: 'Madeira Park', region: 'sea-to-sky-sunshine-coast', count: 39 },
  { slug: 'mill-bay', name: 'Mill Bay', region: 'vancouver-island', count: 38 },
  { slug: 'greenwood', name: 'Greenwood', region: 'okanagan-thompson', count: 37 },
  { slug: 'tofino', name: 'Tofino', region: 'vancouver-island', count: 37 },
  { slug: 'savary-island', name: 'Savary Island', region: 'sea-to-sky-sunshine-coast', count: 37 },
  { slug: 'panorama', name: 'Panorama', region: 'kootenays-rockies', count: 36 },
  { slug: 'kaleden', name: 'Kaleden', region: 'okanagan-thompson', count: 36 },
  { slug: 'lillooet', name: 'Lillooet', region: 'other-bc', count: 36 },
  { slug: 'elkford', name: 'Elkford', region: 'kootenays-rockies', count: 35 },
  { slug: 'texada-island', name: 'Texada Island', region: 'sea-to-sky-sunshine-coast', count: 35 },
  { slug: 'ashcroft', name: 'Ashcroft', region: 'other-bc', count: 35 },
  { slug: 'telkwa', name: 'Telkwa', region: 'northern-bc', count: 34 },
  { slug: 'pender-harbour', name: 'Pender Harbour', region: 'sea-to-sky-sunshine-coast', count: 34 },
  { slug: 'kaslo', name: 'Kaslo', region: 'kootenays-rockies', count: 34 },
  { slug: 'black-creek', name: 'Black Creek', region: 'vancouver-island', count: 33 },
  { slug: 'celista', name: 'Celista', region: 'okanagan-thompson', count: 33 },
  { slug: 'cherryville', name: 'Cherryville', region: 'okanagan-thompson', count: 33 },
  { slug: 'fruitvale', name: 'Fruitvale', region: 'kootenays-rockies', count: 33 },
  { slug: 'deka-lake-sulphurous-hathaway-lakes', name: 'Deka Lake / Sulphurous / Hathaway Lakes', region: 'northern-bc', count: 33 },
  { slug: 'hornby-island', name: 'Hornby Island', region: 'vancouver-island', count: 33 },
  { slug: 'metchosin', name: 'Metchosin', region: 'vancouver-island', count: 32 },
  { slug: 'cache-creek', name: 'Cache Creek', region: 'other-bc', count: 32 },
  { slug: 'beaverdell', name: 'Beaverdell', region: 'okanagan-thompson', count: 31 },
  { slug: 'canim-lake', name: 'Canim Lake', region: 'northern-bc', count: 31 },
  { slug: 'yarrow', name: 'Yarrow', region: 'fraser-valley', count: 31 },
  { slug: 'quadra-island', name: 'Quadra Island', region: 'vancouver-island', count: 30 },
  { slug: 'slocan', name: 'Slocan', region: 'kootenays-rockies', count: 30 },
  { slug: 'winlaw', name: 'Winlaw', region: 'kootenays-rockies', count: 29 },
  { slug: 'ladner', name: 'Ladner', region: 'metro-vancouver', count: 29 },
  { slug: 'sayward', name: 'Sayward', region: 'vancouver-island', count: 29 },
  { slug: 'mackenzie', name: 'Mackenzie', region: 'northern-bc', count: 29 },
  { slug: 'gold-river', name: 'Gold River', region: 'vancouver-island', count: 28 },
  { slug: 'mudge-island', name: 'Mudge Island', region: 'vancouver-island', count: 28 },
  { slug: 'bowser', name: 'Bowser', region: 'vancouver-island', count: 28 },
  { slug: 'forest-grove', name: 'Forest Grove', region: 'northern-bc', count: 28 },
  { slug: 'salmo', name: 'Salmo', region: 'kootenays-rockies', count: 27 },
  { slug: 'cawston', name: 'Cawston', region: 'okanagan-thompson', count: 26 },
  { slug: 'gambier-island', name: 'Gambier Island', region: 'sea-to-sky-sunshine-coast', count: 26 },
  { slug: 'canal-flats', name: 'Canal Flats', region: 'kootenays-rockies', count: 26 },
  { slug: 'taylor', name: 'Taylor', region: 'northern-bc', count: 26 },
  { slug: 'houston', name: 'Houston', region: 'northern-bc', count: 25 },
  { slug: 'crofton', name: 'Crofton', region: 'vancouver-island', count: 25 },
  { slug: 'port-renfrew', name: 'Port Renfrew', region: 'vancouver-island', count: 25 },
  { slug: 'youbou', name: 'Youbou', region: 'vancouver-island', count: 25 },
  { slug: 'malakwa', name: 'Malakwa', region: 'okanagan-thompson', count: 25 },
  { slug: 'fanny-bay', name: 'Fanny Bay', region: 'vancouver-island', count: 24 },
  { slug: 'lindell-beach', name: 'Lindell Beach', region: 'fraser-valley', count: 24 },
  { slug: 'pouce-coupe', name: 'Pouce Coupe', region: 'northern-bc', count: 24 },
  { slug: 'charlie-lake', name: 'Charlie Lake', region: 'northern-bc', count: 24 },
  { slug: 'wardner', name: 'Wardner', region: 'kootenays-rockies', count: 24 },
  { slug: 'crawford-bay', name: 'Crawford Bay', region: 'kootenays-rockies', count: 23 },
  { slug: 'lister', name: 'Lister', region: 'kootenays-rockies', count: 23 },
  { slug: 'pritchard', name: 'Pritchard', region: 'okanagan-thompson', count: 22 },
  { slug: '70-mile-house', name: '70 Mile House', region: 'northern-bc', count: 22 },
  { slug: 'chilcotin', name: 'Chilcotin', region: 'northern-bc', count: 22 },
  { slug: 'horse-lake', name: 'Horse Lake', region: 'northern-bc', count: 22 },
  { slug: 'horsefly', name: 'Horsefly', region: 'northern-bc', count: 22 },
  { slug: 'adams-lake', name: 'Adams Lake', region: 'okanagan-thompson', count: 21 },
  { slug: 'midway', name: 'Midway', region: 'okanagan-thompson', count: 21 },
  { slug: 'rock-creek', name: 'Rock Creek', region: 'okanagan-thompson', count: 21 },
  { slug: 'falkland', name: 'Falkland', region: 'kootenays-rockies', count: 21 },
  { slug: 'balfour', name: 'Balfour', region: 'kootenays-rockies', count: 20 },
  { slug: 'french-creek', name: 'French Creek', region: 'vancouver-island', count: 20 },
  { slug: 'seymour-arm', name: 'Seymour Arm', region: 'okanagan-thompson', count: 20 },
  { slug: 'mcbride', name: 'McBride', region: 'northern-bc', count: 19 },
  { slug: 'protection-island', name: 'Protection Island', region: 'vancouver-island', count: 19 },
  { slug: 'highlands', name: 'Highlands', region: 'vancouver-island', count: 19 },
  { slug: 'new-denver', name: 'New Denver', region: 'kootenays-rockies', count: 19 },
  { slug: 'cortes-island', name: 'Cortes Island', region: 'vancouver-island', count: 19 },
  { slug: 'denman-island', name: 'Denman Island', region: 'vancouver-island', count: 19 },
  { slug: 'westbridge', name: 'Westbridge', region: 'okanagan-thompson', count: 19 },
  { slug: 'salt-spring-island', name: 'Salt Spring Island', region: 'vancouver-island', count: 18 },
  { slug: 'genelle', name: 'Genelle', region: 'kootenays-rockies', count: 18 },
  { slug: 'wynndel', name: 'Wynndel', region: 'kootenays-rockies', count: 18 },
  { slug: 'errington', name: 'Errington', region: 'vancouver-island', count: 18 },
  { slug: 'fort-st-james', name: 'Fort St. James', region: 'northern-bc', count: 18 },
  { slug: 'magna-bay', name: 'Magna Bay', region: 'okanagan-thompson', count: 17 },
  { slug: 'apex-mountain', name: 'Apex Mountain', region: 'okanagan-thompson', count: 17 },
  { slug: 'wasa', name: 'Wasa', region: 'kootenays-rockies', count: 16 },
  { slug: 'warfield', name: 'Warfield', region: 'kootenays-rockies', count: 16 },
  { slug: 'erickson', name: 'Erickson', region: 'kootenays-rockies', count: 16 },
  { slug: 'lions-bay', name: 'Lions Bay', region: 'metro-vancouver', count: 16 },
  { slug: 'clinton', name: 'Clinton', region: 'other-bc', count: 16 },
  { slug: 'lasqueti-island', name: 'Lasqueti Island', region: 'sea-to-sky-sunshine-coast', count: 16 },
  { slug: 'procter', name: 'Procter', region: 'kootenays-rockies', count: 16 },
  { slug: 'bella-coola', name: 'Bella Coola', region: 'northern-bc', count: 15 },
  { slug: 'saltair', name: 'Saltair', region: 'vancouver-island', count: 14 },
  { slug: 'hudsons-hope', name: 'Hudsons Hope', region: 'northern-bc', count: 14 },
  { slug: 'grindrod', name: 'Grindrod', region: 'okanagan-thompson', count: 14 },
  { slug: '150-mile-house', name: '150 Mile House', region: 'northern-bc', count: 14 },
  { slug: 'tahsis', name: 'Tahsis', region: 'vancouver-island', count: 14 },
  { slug: 'loon-lake', name: 'Loon Lake', region: 'other-bc', count: 14 },
  { slug: 'hixon', name: 'Hixon', region: 'northern-bc', count: 14 },
  { slug: 'fintry', name: 'Fintry', region: 'okanagan-thompson', count: 14 },
  { slug: 'likely', name: 'Likely', region: 'northern-bc', count: 13 },
  { slug: 'granisle', name: 'Granisle', region: 'northern-bc', count: 13 },
  { slug: 'malahat', name: 'Malahat', region: 'vancouver-island', count: 13 },
  { slug: 'edgewater', name: 'Edgewater', region: 'kootenays-rockies', count: 13 },
  { slug: 'montrose', name: 'Montrose', region: 'kootenays-rockies', count: 13 },
  { slug: 'canyon', name: 'Canyon', region: 'kootenays-rockies', count: 13 },
  { slug: 'sointula', name: 'Sointula', region: 'vancouver-island', count: 13 },
  { slug: 'silver-star', name: 'Silver Star', region: 'okanagan-thompson', count: 13 },
  { slug: 'yahk', name: 'Yahk', region: 'kootenays-rockies', count: 12 },
  { slug: 'cluculz-lake', name: 'Cluculz Lake', region: 'northern-bc', count: 12 },
  { slug: 'honeymoon-bay', name: 'Honeymoon Bay', region: 'vancouver-island', count: 12 },
  { slug: 'columbia-valley', name: 'Columbia Valley', region: 'fraser-valley', count: 12 },
  { slug: 'hazelton', name: 'Hazelton', region: 'northern-bc', count: 12 },
  { slug: 'fauquier', name: 'Fauquier', region: 'kootenays-rockies', count: 11 },
  { slug: 'riondel', name: 'Riondel', region: 'kootenays-rockies', count: 11 },
  { slug: 'westwold', name: 'Westwold', region: 'okanagan-thompson', count: 11 },
  { slug: 'appledale', name: 'Appledale', region: 'kootenays-rockies', count: 11 },
  { slug: 'sidney-island', name: 'Sidney Island', region: 'vancouver-island', count: 11 },
  { slug: 'belcarra', name: 'Belcarra', region: 'metro-vancouver', count: 11 },
  { slug: '103-mile-house', name: '103 Mile House', region: 'northern-bc', count: 11 },
  { slug: 'mara', name: 'Mara', region: 'okanagan-thompson', count: 11 },
  { slug: 'port-mcneill', name: 'Port McNeill', region: 'vancouver-island', count: 11 },
  { slug: 'hilliers', name: 'Hilliers', region: 'vancouver-island', count: 11 },
  { slug: 'keats-island', name: 'Keats Island', region: 'sea-to-sky-sunshine-coast', count: 11 },
  { slug: 'new-hazelton', name: 'New Hazelton', region: 'northern-bc', count: 11 },
  { slug: 'port-alice', name: 'Port Alice', region: 'vancouver-island', count: 10 },
  { slug: 'masset', name: 'Masset', region: 'northern-bc', count: 10 },
  { slug: 'union-bay', name: 'Union Bay', region: 'vancouver-island', count: 10 },
  { slug: 'sardis', name: 'Sardis', region: 'fraser-valley', count: 10 },
  { slug: 'burton', name: 'Burton', region: 'kootenays-rockies', count: 10 },
  { slug: 'slocan-park', name: 'Slocan Park', region: 'kootenays-rockies', count: 10 },
  { slug: 'lytton', name: 'Lytton', region: 'other-bc', count: 10 },
  { slug: 'port-clements', name: 'Port Clements', region: 'northern-bc', count: 10 },
  { slug: 'jaffray', name: 'Jaffray', region: 'kootenays-rockies', count: 10 },
  { slug: 'sheridan-lake', name: 'Sheridan Lake', region: 'northern-bc', count: 10 },
  { slug: 'ruxton-island', name: 'Ruxton Island', region: 'vancouver-island', count: 9 },
  { slug: 'green-lake', name: 'Green Lake', region: 'northern-bc', count: 9 },
  { slug: 'edgewood', name: 'Edgewood', region: 'kootenays-rockies', count: 9 },
  { slug: 'bridesville', name: 'Bridesville', region: 'okanagan-thompson', count: 9 },
  { slug: 'royston', name: 'Royston', region: 'vancouver-island', count: 9 },
  { slug: 'sardis-greendale', name: 'Sardis - Greendale', region: 'fraser-valley', count: 9 },
  { slug: 'robson', name: 'Robson', region: 'kootenays-rockies', count: 9 },
  { slug: 'britannia-beach', name: 'Britannia Beach', region: 'sea-to-sky-sunshine-coast', count: 9 },
  { slug: 'merville', name: 'Merville', region: 'vancouver-island', count: 9 },
  { slug: 'swansea-point', name: 'Swansea Point', region: 'okanagan-thompson', count: 9 },
  { slug: 'cassidy', name: 'Cassidy', region: 'vancouver-island', count: 8 },
  { slug: 'harrop', name: 'Harrop', region: 'kootenays-rockies', count: 8 },
  { slug: 'kitchener', name: 'Kitchener', region: 'kootenays-rockies', count: 8 },
  { slug: 'd-arcy', name: 'D\'Arcy', region: 'sea-to-sky-sunshine-coast', count: 8 },
  { slug: 'silverton', name: 'Silverton', region: 'kootenays-rockies', count: 8 },
  { slug: 'coombs', name: 'Coombs', region: 'vancouver-island', count: 8 },
  { slug: 'south-slocan', name: 'South Slocan', region: 'kootenays-rockies', count: 8 },
  { slug: 'boswell', name: 'Boswell', region: 'kootenays-rockies', count: 8 },
  { slug: 'bonnington', name: 'Bonnington', region: 'kootenays-rockies', count: 7 },
  { slug: 'tulameen', name: 'Tulameen', region: 'okanagan-thompson', count: 7 },
  { slug: 'topley', name: 'Topley', region: 'northern-bc', count: 7 },
  { slug: 'gold-bridge', name: 'Gold Bridge', region: 'other-bc', count: 7 },
  { slug: 'hagensborg', name: 'Hagensborg', region: 'northern-bc', count: 7 },
  { slug: 'daajing-giids-city', name: 'Daajing Giids City', region: 'northern-bc', count: 7 },
  { slug: 'oyama', name: 'Oyama', region: 'okanagan-thompson', count: 7 },
  { slug: 'fort-steele', name: 'Fort Steele', region: 'kootenays-rockies', count: 7 },
  { slug: 'hedley', name: 'Hedley', region: 'okanagan-thompson', count: 7 },
  { slug: 'boston-bar-lytton', name: 'Boston Bar / Lytton', region: 'other-bc', count: 7 },
  { slug: 'vavenby', name: 'Vavenby', region: 'northern-bc', count: 7 },
  { slug: 'little-fort', name: 'Little Fort', region: 'okanagan-thompson', count: 7 },
  { slug: 'ainsworth', name: 'Ainsworth', region: 'kootenays-rockies', count: 7 },
  { slug: 'bamfield', name: 'Bamfield', region: 'vancouver-island', count: 6 },
  { slug: 'kitwanga', name: 'Kitwanga', region: 'northern-bc', count: 6 },
  { slug: 'fort-fraser', name: 'Fort Fraser', region: 'northern-bc', count: 6 },
  { slug: 'moberly-lake', name: 'Moberly Lake', region: 'northern-bc', count: 6 },
  { slug: 'thetis-island', name: 'Thetis Island', region: 'vancouver-island', count: 6 },
  { slug: 'sardis-chwk-river-valley', name: 'Sardis - Chwk River Valley', region: 'fraser-valley', count: 6 },
  { slug: 'rosebery', name: 'Rosebery', region: 'kootenays-rockies', count: 6 },
  { slug: 'coalmont-tulameen', name: 'Coalmont-Tulameen', region: 'okanagan-thompson', count: 6 },
  { slug: 'nelson-island', name: 'Nelson Island', region: 'sea-to-sky-sunshine-coast', count: 6 },
  { slug: 'egmont', name: 'Egmont', region: 'sea-to-sky-sunshine-coast', count: 6 },
  { slug: 'kootenay-bay', name: 'Kootenay Bay', region: 'kootenays-rockies', count: 6 },
  { slug: 'arrow-park', name: 'Arrow Park', region: 'kootenays-rockies', count: 6 },
  { slug: 'hyde-creek', name: 'Hyde Creek', region: 'vancouver-island', count: 6 },
  { slug: 'meadow-creek', name: 'Meadow Creek', region: 'kootenays-rockies', count: 6 },
  { slug: 'spillimacheen', name: 'Spillimacheen', region: 'kootenays-rockies', count: 6 },
  { slug: 'saturna-island', name: 'Saturna Island', region: 'vancouver-island', count: 5 },
  { slug: 'blue-river', name: 'Blue River', region: 'northern-bc', count: 5 },
  { slug: 'okanagan-landing', name: 'Okanagan Landing', region: 'okanagan-thompson', count: 5 },
  { slug: 'baynes-lake', name: 'Baynes Lake', region: 'kootenays-rockies', count: 5 },
  { slug: 'bralorne', name: 'Bralorne', region: 'other-bc', count: 5 },
  { slug: 'decourcy-island', name: 'Decourcy Island', region: 'vancouver-island', count: 5 },
  { slug: 'bear-lake', name: 'Bear Lake', region: 'northern-bc', count: 5 },
  { slug: 'olalla', name: 'Olalla', region: 'okanagan-thompson', count: 5 },
  { slug: 'ymir', name: 'Ymir', region: 'kootenays-rockies', count: 5 },
  { slug: 'passmore', name: 'Passmore', region: 'kootenays-rockies', count: 5 },
  { slug: 'yale', name: 'Yale', region: 'other-bc', count: 5 },
  { slug: 'furry-creek', name: 'Furry Creek', region: 'metro-vancouver', count: 5 },
  { slug: 'quatsino', name: 'Quatsino', region: 'vancouver-island', count: 5 },
  { slug: 'sunnybrae', name: 'Sunnybrae', region: 'okanagan-thompson', count: 5 },
  { slug: 'port-edward', name: 'Port Edward', region: 'northern-bc', count: 5 },
  { slug: 'krestova', name: 'Krestova', region: 'kootenays-rockies', count: 4 },
  { slug: 'laidlaw', name: 'Laidlaw', region: 'fraser-valley', count: 4 },
  { slug: 'joe-rich', name: 'Joe Rich', region: 'okanagan-thompson', count: 4 },
  { slug: 'harrison-mills', name: 'Harrison Mills', region: 'fraser-valley', count: 4 },
  { slug: 'st-ives', name: 'St. Ives', region: 'okanagan-thompson', count: 4 },
  { slug: 'ryder-lake', name: 'Ryder Lake', region: 'fraser-valley', count: 4 },
  { slug: 'stump-lake', name: 'Stump Lake', region: 'okanagan-thompson', count: 4 },
  { slug: 'parson', name: 'Parson', region: 'kootenays-rockies', count: 4 },
  { slug: 'summit-lake', name: 'Summit Lake', region: 'kootenays-rockies', count: 4 },
  { slug: 'stewart', name: 'Stewart', region: 'northern-bc', count: 4 },
  { slug: 'mclure', name: 'McLure', region: 'okanagan-thompson', count: 4 },
  { slug: 'skookumchuck', name: 'Skookumchuck', region: 'kootenays-rockies', count: 4 },
  { slug: 'lardeau', name: 'Lardeau', region: 'kootenays-rockies', count: 4 },
  { slug: 'skidegate-sandspit', name: 'Skidegate/Sandspit', region: 'northern-bc', count: 4 },
  { slug: 'lake-koocanusa', name: 'Lake Koocanusa', region: 'kootenays-rockies', count: 4 },
  { slug: 'elko', name: 'Elko', region: 'kootenays-rockies', count: 4 },
  { slug: 'crescent-valley', name: 'Crescent Valley', region: 'kootenays-rockies', count: 4 },
  { slug: 'winter-harbour', name: 'Winter Harbour', region: 'vancouver-island', count: 4 },
  { slug: 'gray-creek', name: 'Gray Creek', region: 'kootenays-rockies', count: 4 },
  { slug: 'read-island', name: 'Read Island', region: 'vancouver-island', count: 4 },
  { slug: 'rutland', name: 'Rutland', region: 'okanagan-thompson', count: 4 },
  { slug: 'mcleese-lake', name: 'McLeese Lake', region: 'northern-bc', count: 3 },
  { slug: 'moyie', name: 'Moyie', region: 'kootenays-rockies', count: 3 },
  { slug: 'canoe', name: 'Canoe', region: 'okanagan-thompson', count: 3 },
  { slug: 'grasmere', name: 'Grasmere', region: 'kootenays-rockies', count: 3 },
  { slug: 'wilmer', name: 'Wilmer', region: 'kootenays-rockies', count: 3 },
  { slug: 'shoreacres', name: 'Shoreacres', region: 'kootenays-rockies', count: 3 },
  { slug: 'arrow-creek', name: 'Arrow Creek', region: 'kootenays-rockies', count: 3 },
  { slug: 'trout-lake', name: 'Trout Lake', region: 'kootenays-rockies', count: 3 },
  { slug: 'mount-baldy', name: 'Mount Baldy', region: 'okanagan-thompson', count: 3 },
  { slug: 'whiskey-creek', name: 'Whiskey Creek', region: 'vancouver-island', count: 3 },
  { slug: 'renata', name: 'Renata', region: 'kootenays-rockies', count: 3 },
  { slug: 'fletcher-creek', name: 'Fletcher Creek', region: 'kootenays-rockies', count: 3 },
  { slug: 'kuskanook', name: 'Kuskanook', region: 'kootenays-rockies', count: 3 },
  { slug: 'deer-park', name: 'Deer Park', region: 'kootenays-rockies', count: 3 },
  { slug: 'daajing-giids-rural', name: 'Daajing Giids Rural', region: 'northern-bc', count: 3 },
  { slug: 'anstey-arm', name: 'Anstey Arm', region: 'okanagan-thompson', count: 3 },
  { slug: 'wells', name: 'Wells', region: 'northern-bc', count: 3 },
  { slug: 'thornhill', name: 'Thornhill', region: 'northern-bc', count: 3 },
  { slug: 'kingsgate', name: 'Kingsgate', region: 'kootenays-rockies', count: 3 },
  { slug: 'other-islands', name: 'Other Islands', region: 'vancouver-island', count: 3 },
  { slug: 'ta-ta-creek', name: 'Ta Ta Creek', region: 'kootenays-rockies', count: 3 },
  { slug: 'brisco', name: 'Brisco', region: 'kootenays-rockies', count: 2 },
  { slug: 'nelway', name: 'Nelway', region: 'kootenays-rockies', count: 2 },
  { slug: 'sanca', name: 'Sanca', region: 'kootenays-rockies', count: 2 },
  { slug: 'galena-bay', name: 'Galena Bay', region: 'kootenays-rockies', count: 2 },
  { slug: 'beaton', name: 'Beaton', region: 'kootenays-rockies', count: 2 },
  { slug: 'westholme', name: 'Westholme', region: 'vancouver-island', count: 2 },
  { slug: 'sonora-island', name: 'Sonora Island', region: 'vancouver-island', count: 2 },
  { slug: 'rivervale', name: 'Rivervale', region: 'kootenays-rockies', count: 2 },
  { slug: 'lakelse-lake', name: 'Lakelse Lake', region: 'northern-bc', count: 2 },
  { slug: 'mabel-lake', name: 'Mabel Lake', region: 'okanagan-thompson', count: 2 },
  { slug: 'bednesti', name: 'Bednesti', region: 'northern-bc', count: 2 },
  { slug: 'jewel-lake', name: 'Jewel Lake', region: 'okanagan-thompson', count: 2 },
  { slug: 'ross-spur', name: 'Ross Spur', region: 'kootenays-rockies', count: 2 },
  { slug: 'prevost-island', name: 'Prevost Island', region: 'vancouver-island', count: 2 },
  { slug: 'woss', name: 'Woss', region: 'vancouver-island', count: 2 },
  { slug: 'twin-bays', name: 'Twin Bays', region: 'kootenays-rockies', count: 2 },
  { slug: 'johnsons-landing', name: 'Johnsons Landing', region: 'kootenays-rockies', count: 2 },
  { slug: 'hills', name: 'Hills', region: 'kootenays-rockies', count: 2 },
  { slug: 'hosmer', name: 'Hosmer', region: 'kootenays-rockies', count: 2 },
  { slug: 'dry-gulch', name: 'Dry Gulch', region: 'kootenays-rockies', count: 2 },
  { slug: 'ootischenia', name: 'Ootischenia', region: 'kootenays-rockies', count: 2 },
  { slug: 'needles', name: 'Needles', region: 'kootenays-rockies', count: 2 },
  { slug: 'ferguson', name: 'Ferguson', region: 'kootenays-rockies', count: 2 },
  { slug: 'bouchie-lake', name: 'Bouchie Lake', region: 'northern-bc', count: 2 },
  { slug: 'carmi', name: 'Carmi', region: 'okanagan-thompson', count: 2 },
  { slug: 'taghum', name: 'Taghum', region: 'kootenays-rockies', count: 2 },
  { slug: 'zeballos', name: 'Zeballos', region: 'vancouver-island', count: 2 },
  { slug: 'nootka-island', name: 'Nootka Island', region: 'vancouver-island', count: 2 },
  { slug: 'thrums', name: 'Thrums', region: 'kootenays-rockies', count: 2 },
  { slug: 'malcolm-island', name: 'Malcolm Island', region: 'vancouver-island', count: 2 },
  { slug: 'argenta', name: 'Argenta', region: 'kootenays-rockies', count: 2 },
  { slug: 'panorama-mountain', name: 'Panorama Mountain', region: 'kootenays-rockies', count: 2 },
  { slug: 'coal-harbour', name: 'Coal Harbour', region: 'vancouver-island', count: 2 },
  { slug: 'white-lake', name: 'White Lake', region: 'okanagan-thompson', count: 2 },
  { slug: 'dome-creek', name: 'Dome Creek', region: 'northern-bc', count: 2 },
  { slug: 'shelley', name: 'Shelley', region: 'northern-bc', count: 2 },
  { slug: 'atlin', name: 'Atlin', region: 'northern-bc', count: 2 },
  { slug: 'spur-valley', name: 'Spur Valley', region: 'kootenays-rockies', count: 2 },
  { slug: 'red-bluff-dragon-lake', name: 'Red Bluff / Dragon Lake', region: 'northern-bc', count: 2 },
  { slug: 'bonnington-falls', name: 'Bonnington Falls', region: 'kootenays-rockies', count: 2 },
  { slug: 'marysville', name: 'Marysville', region: 'kootenays-rockies', count: 2 },
  { slug: 'south-francois', name: 'South Francois', region: 'northern-bc', count: 1 },
  { slug: 'cascade', name: 'Cascade', region: 'kootenays-rockies', count: 1 },
  { slug: 'red-rock-stoner', name: 'Red Rock / Stoner', region: 'northern-bc', count: 1 },
  { slug: 'gulf-islands', name: 'Gulf Islands', region: 'vancouver-island', count: 1 },
  { slug: 'mt-woodside', name: 'Mt Woodside', region: 'fraser-valley', count: 1 },
  { slug: 'newgate', name: 'Newgate', region: 'kootenays-rockies', count: 1 },
  { slug: 'lakeside-rural', name: 'Lakeside Rural', region: 'northern-bc', count: 1 },
  { slug: 'university-endowment-lands', name: 'University Endowment Lands', region: 'metro-vancouver', count: 1 },
  { slug: 'mount-currie', name: 'Mount Currie', region: 'sea-to-sky-sunshine-coast', count: 1 },
  { slug: 'langdale', name: 'Langdale', region: 'sea-to-sky-sunshine-coast', count: 1 },
  { slug: 'rosswood-nass', name: 'Rosswood Nass', region: 'northern-bc', count: 1 },
  { slug: 'tomslake', name: 'Tomslake', region: 'northern-bc', count: 1 },
  { slug: 'lakeview-heights', name: 'Lakeview Heights', region: 'okanagan-thompson', count: 1 },
  { slug: 'birken', name: 'Birken', region: 'other-bc', count: 1 },
  { slug: 'sirdar', name: 'Sirdar', region: 'kootenays-rockies', count: 1 },
  { slug: 'nazko', name: 'Nazko', region: 'northern-bc', count: 1 },
  { slug: 'endako', name: 'Endako', region: 'northern-bc', count: 1 },
  { slug: 'schroeder-creek', name: 'Schroeder Creek', region: 'kootenays-rockies', count: 1 },
  { slug: 'wonowon', name: 'Wonowon', region: 'northern-bc', count: 1 },
  { slug: 'nukko-lake', name: 'Nukko Lake', region: 'northern-bc', count: 1 },
  { slug: 'beasley', name: 'Beasley', region: 'kootenays-rockies', count: 1 },
  { slug: 'telegraph-cove', name: 'Telegraph Cove', region: 'vancouver-island', count: 1 },
  { slug: 'tarrys', name: 'Tarrys', region: 'kootenays-rockies', count: 1 },
  { slug: 'dunster', name: 'Dunster', region: 'northern-bc', count: 1 },
  { slug: 'cecil-lake', name: 'Cecil Lake', region: 'northern-bc', count: 1 },
  { slug: 'wycliffe', name: 'Wycliffe', region: 'kootenays-rockies', count: 1 },
  { slug: 'lemon-creek', name: 'Lemon Creek', region: 'kootenays-rockies', count: 1 },
  { slug: 'mirror-lake', name: 'Mirror Lake', region: 'kootenays-rockies', count: 1 },
  { slug: 'west-thurlow-island', name: 'West Thurlow Island', region: 'vancouver-island', count: 1 },
  { slug: 'boston-bar', name: 'Boston Bar', region: 'other-bc', count: 1 },
  { slug: 'stuart-island', name: 'Stuart Island', region: 'vancouver-island', count: 1 },
  { slug: 'poplar-creek', name: 'Poplar Creek', region: 'kootenays-rockies', count: 1 },
  { slug: 'kelsey-bay', name: 'Kelsey Bay', region: 'vancouver-island', count: 1 },
  { slug: 'west-cracroft-island', name: 'West Cracroft Island', region: 'vancouver-island', count: 1 },
  { slug: 'ashton-creek', name: 'Ashton Creek', region: 'okanagan-thompson', count: 1 },
  { slug: 'north-shuswap', name: 'North Shuswap', region: 'okanagan-thompson', count: 1 },
  { slug: 'field', name: 'Field', region: 'kootenays-rockies', count: 1 },
  { slug: 'castledale', name: 'Castledale', region: 'kootenays-rockies', count: 1 },
  { slug: 'wells-barkerville', name: 'Wells / Barkerville', region: 'northern-bc', count: 1 },
  { slug: 'valleyview', name: 'Valleyview', region: 'northern-bc', count: 1 },
  { slug: 'ness-lake', name: 'Ness Lake', region: 'northern-bc', count: 1 },
  { slug: 'farmington', name: 'Farmington', region: 'northern-bc', count: 1 },
  { slug: 'goodlow', name: 'Goodlow', region: 'northern-bc', count: 1 },
  { slug: 'esler-dog-creek', name: 'Esler / Dog Creek', region: 'northern-bc', count: 1 },
  { slug: 'alert-bay', name: 'Alert Bay', region: 'vancouver-island', count: 1 },
  { slug: 'glade', name: 'Glade', region: 'kootenays-rockies', count: 1 },
  { slug: 'mount-alder', name: 'Mount Alder', region: 'northern-bc', count: 1 },
  { slug: 'queens-bay', name: 'Queens Bay', region: 'kootenays-rockies', count: 1 },
  { slug: 'oyster-river', name: 'Oyster River', region: 'vancouver-island', count: 1 },
  { slug: 'east-thurlow-island', name: 'East Thurlow Island', region: 'vancouver-island', count: 1 },
  { slug: 'howser', name: 'Howser', region: 'kootenays-rockies', count: 1 },
  { slug: 'piers-island', name: 'Piers Island', region: 'vancouver-island', count: 1 },
  { slug: 'monte-lake', name: 'Monte Lake', region: 'okanagan-thompson', count: 1 },
  { slug: 'edgewood-terrace', name: 'Edgewood Terrace', region: 'northern-bc', count: 1 },
  { slug: 'craigellachie', name: 'Craigellachie', region: 'okanagan-thompson', count: 1 },
  { slug: 'rolla', name: 'Rolla', region: 'northern-bc', count: 1 },
  { slug: 'whaletown', name: 'Whaletown', region: 'vancouver-island', count: 1 },
  { slug: 'lavington', name: 'Lavington', region: 'okanagan-thompson', count: 1 },
  { slug: 'mahood-lake', name: 'Mahood Lake', region: 'northern-bc', count: 1 },
];

export const BC_CITIES_BY_SLUG: ReadonlyMap<string, BcCity> = new Map(
  BC_CITIES.map((c) => [c.slug, c] as const),
);

const BY_NAME: ReadonlyMap<string, BcCity> = new Map(
  BC_CITIES.map((c) => [c.name.toLowerCase(), c] as const),
);

/** Registry entry for an exact feed city name, if it has inventory. */
export function bcCityByName(name: string): BcCity | undefined {
  return BY_NAME.get(name.trim().toLowerCase());
}

const BY_REGION = new Map<BcRegionSlug, BcCity[]>();
for (const c of BC_CITIES) {
  const list = BY_REGION.get(c.region);
  if (list) list.push(c);
  else BY_REGION.set(c.region, [c]);
}

/** Every city in a BC super-region, inventory-ordered. Province-wide region
 *  tokens resolve through this so null-area listings stay reachable. */
export function citiesInRegion(region: BcRegionSlug): readonly BcCity[] {
  return BY_REGION.get(region) ?? [];
}

/** Exact `city` values a region token resolves to (for the IN filter). */
export function cityNamesForRegion(region: BcRegionSlug): string[] {
  return citiesInRegion(region).map((c) => c.name);
}
